import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../contexts/SessionContext';
import API_URL from '../constants/environment';
import { Action } from './useGetOrdersFeed';
import { PartStatus, OrderNote, ReviewedByUser, SupplierNote, OrderEmsSummary } from './useGetOrderDetail';

export interface SelectionPart {
    _id: string;
    flag?: boolean;
    flagReason?: string;
    excludeOrdering?: boolean;
    partNumber: string;
    title: string;
    delivery: number | null;
    deliveryString: string | null;
    listPrice: number | null;
    buyPrice: number | null;
    listPriceAdjusted?: boolean;
}

interface SelectionSection {
    totalParts: number;
    deliveryString: string | null;
    orderValue: number | null;
    grossMargin: number | null;
    parts: Array<SelectionPart>;
}

export interface OrderedPart {
    _id: string;
    partNumber: string;
    correctedPartNumber?: string;
    title: string;
    eta: string;
    correctedEta?: string;
    listPrice: number;
    buyPrice: number;
    correctedBuyPrice?: number;
    correctedListPrice?: number;
    delivery: number | null;
    cancelled?: boolean;
    returned?: boolean;
    canReturn: boolean;
}

export interface FullSupplierOrder {
    supplierOrderId: string;
    totalParts: number;
    eta: string;
    orderValue: number;
    grossMargin: number;
    parts: Array<OrderedPart>;
    title: string;
    source?: 'opstrax' | 'partstrader' | 'ccc' | 'tesla' | 'inventory';
    displayId: string | number | null;
    supplierEmail: string | null;
    supplierPhone: string | null;
    timePlaced: string;
    status: PartStatus | 'rejected' | 'cancelled';
    hasChanges?: boolean;
    /**
     * The manual Mark Confirmed action applies: a PO still at Ordered on any
     * marketplace, with at least one part not cancelled or returned.
     */
    canManualConfirm?: boolean;
    awaitingPartstraderConfirmation?: boolean;
}

interface OrderedSection {
    orders: Array<FullSupplierOrder>;
}

export interface OrderDrawerResponse {
    roNumber: string;
    vehicle: string;
    daysActive: string;
    partsOrdered: number;
    partsConfirmed: number;
    totalParts: number;
    orderValue: number | null;
    grossMargin: number | null;
    minGrossMarginThreshold?: number | null;
    actions: Array<Action>;
    manuallyFlagged: boolean;
    quotesOnly: boolean;
    archived: boolean;
    completed: boolean;
    potentialTotal: boolean;
    preOrderBackorder?: boolean;
    quoteRequestAllowedAutoOrder?: boolean;
    oemEndorsement: boolean;
    dealerReferral?: string;
    reviewedByUsers: Array<ReviewedByUser>;
    notes: Array<OrderNote>;
    supplierNotes: Array<SupplierNote>;
    supplierMessageCounts: Record<string, number>;
    vin: string;
    insurance: string | null;
    timeCreated: Date;
    location: string;
    customer: string | null;
    claimNumber: string | null;
    mileage?: number;
    drpJob: boolean;
    lastUpdated: Date;
    /** Shared EMS summary — same shape as POST /order/detail `ems`. */
    ems: OrderEmsSummary;
    quotes?: {
        total: number;
        quotes: Array<{
            _id: string;
            timestamp: Date;
            totalParts: number;
            status: 'open' | 'closed';
            submittedBy: string;
            preOrder: boolean;
            autoOrder: boolean;
            estReturnTime?: Date | null;
        }>;
    };
    selection?: {
        total: number;
        pending?: SelectionSection;
        flagged?: SelectionSection;
        noQuoted?: SelectionSection;
    };
    ordered?: {
        total: number;
        ordered?: OrderedSection;
        confirmed?: OrderedSection;
        cancelled?: OrderedSection;
        rejected?: OrderedSection;
    };
    rekey?: {
        totalConfirmed: number;
        totalRekeyed: number;
        partsFlagged: number;
        partsAdvisor: number;
        flaggedParts: Array<{
            _id: string;
            title: string;
            lineNumber: number;
            flagReason?: string;
        }>;
    };
}

const getOrderDrawer = async (orderId: string, sessionId: string | null): Promise<OrderDrawerResponse> => {
    const response = await axios.get(`${API_URL}/order/drawer`, {
        params: { orderId },
        headers: {
            Authorization: sessionId
        }
    });
    return response.data;
};

export const useGetOrderDrawer = (orderId: string | null, isOpen: boolean) => {
    const sessionContext = useContext(SessionContext);

    return useQuery({
        queryKey: ['/order/drawer', orderId],
        queryFn: () => getOrderDrawer(orderId!, sessionContext?.sessionId ?? null),
        enabled: !!orderId && !!sessionContext?.sessionId && isOpen,
    });
};
